import { useState, useEffect } from "react";
import { KeyRound, Loader2, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const label = (f) => f.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const ProcessorKeysButton = ({ row, onStatusChange }) => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState(row.status);
  useEffect(() => { setStatus(row.status); }, [row.status]);
  const [toggling, setToggling] = useState(false);
  const active = status === "active";

  const toggle = async (enabled) => {
    setToggling(true);
    try {
      const { data } = await api.patch(`/processors/${row.id}/toggle`, { enabled });
      setStatus(data.status);
      onStatusChange?.(data.status);
      toast.success(`${row.name} ${data.status === "active" ? "activated" : "deactivated"}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setToggling(false); }
  };

  const toggleControl = (testid) => (
    <span className="inline-flex items-center gap-2">
      <Switch checked={active} onCheckedChange={toggle} disabled={toggling} data-testid={testid}
        className="data-[state=checked]:bg-emerald-500" />
      <span data-testid={`${testid}-label`} className={`text-xs font-medium ${active ? "text-emerald-500" : "text-muted-foreground"}`}>
        {active ? "Active" : "Inactive"}
      </span>
    </span>
  );

  const load = async () => {
    try { const { data } = await api.get(`/processors/${row.id}/credentials`); setInfo(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const openDlg = () => { setOpen(true); setVals({}); setInfo(null); load(); };

  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(""); }
  };
  const save = () => run("save", async () => {
    const { data } = await api.put(`/processors/${row.id}/credentials`, { credentials: vals });
    setInfo(data); setVals({}); toast.success("Keys saved (encrypted)");
  });
  const test = () => run("test", async () => {
    const { data } = await api.post(`/processors/${row.id}/credentials/test`);
    data.ok ? toast.success("Connection verified") : toast.error(`Key rejected (HTTP ${data.http_status || data.error})`);
    load();
  });
  const clear = () => run("clear", async () => {
    await api.delete(`/processors/${row.id}/credentials`); toast.success("Keys removed"); load();
  });

  return (
    <span className="inline-flex items-center gap-3 mr-2">
      {toggleControl("processor-status-toggle")}
      <Button variant="ghost" size="sm" onClick={openDlg} data-testid="processor-keys-button"
        className="text-amber-500 hover:text-amber-400 hover:bg-amber-500/10 mr-1">
        <KeyRound className="h-4 w-4 mr-1" /> Keys
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-md" data-testid="processor-keys-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{row.name} keys</DialogTitle>
            <DialogDescription className="text-muted-foreground">Stored encrypted. Leave a field blank to keep its current value.</DialogDescription>
          </DialogHeader>
          {!info ? <Loader2 className="h-5 w-5 animate-spin mx-auto my-6 text-muted-foreground" /> : (
            <div className="space-y-4">
              <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2">
                <span className="text-sm">Processor status</span>
                {toggleControl("processor-dialog-status-toggle")}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {info.live_adapter
                  ? <Badge data-testid="processor-live-adapter" className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30">Live charging ready</Badge>
                  : <Badge data-testid="processor-live-adapter" className="bg-muted text-muted-foreground border-border">Keys stored · adapter coming</Badge>}
                {info.last_test && (info.last_test.ok
                  ? <span className="text-xs text-emerald-500 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Verified</span>
                  : <span className="text-xs text-red-500 inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5" />Last test failed</span>)}
              </div>
              {info.usage && <p className="text-xs text-muted-foreground">{info.usage}</p>}
              {info.fields.map((f) => (
                <div key={f} className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    {label(f)}{info.required.includes(f) && <span className="text-red-400"> *</span>}
                    {info.configured[f] && <span className="ml-2 font-mono text-foreground">{info.configured[f]}</span>}
                  </Label>
                  <Input data-testid={`processor-key-${f}`} type="password" value={vals[f] || ""} autoComplete="off"
                    onChange={(e) => setVals((p) => ({ ...p, [f]: e.target.value }))} className="bg-muted/40 border-border font-mono"
                    placeholder={info.configured[f] ? "•••• saved" : "Not set"} />
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            {info && Object.keys(info.configured).length > 0 && (
              <Button variant="ghost" onClick={clear} disabled={!!busy} data-testid="processor-keys-clear" className="text-red-500 mr-auto">
                <Trash2 className="h-4 w-4 mr-1" /> Remove
              </Button>
            )}
            {info?.live_adapter && (
              <Button variant="outline" onClick={test} disabled={!!busy || !info.configured.secret_key} data-testid="processor-keys-test" className="border-border">
                {busy === "test" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test"}
              </Button>
            )}
            <Button onClick={save} disabled={!!busy || !Object.values(vals).some((v) => v?.trim())} data-testid="processor-keys-save"
              className="bg-indigo-500 hover:bg-indigo-600 text-white">
              {busy === "save" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save keys"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </span>
  );
};
